import { Outlet } from "react-router-dom";
import { Brand } from "~/components/brand/Brand";
import { Sticker } from "~/components/base/Sticker";
import { useT } from "~/hooks/useT";
import { LANGS } from "~/lib/i18nConfig";

/**
 * AuthLayout
 *
 * Split-screen shell shared by `/login` and `/register` per the React
 * reference (`components/login.jsx` + `components/register.jsx`):
 *
 *   ┌────────────────┬──────────────────────┐
 *   │  Brand   [LANG]│  ★stickers★          │
 *   │                │                      │
 *   │   <Outlet/>    │   display heading    │
 *   │   (form)       │   blurb              │
 *   │                │                      │
 *   │  © Foundation  │                      │
 *   └────────────────┴──────────────────────┘
 *
 * - Layout class `.auth-grid` is `1fr 1.1fr` and collapses to a single
 *   column under 880px (see `globals.css`).
 * - The left half exposes a centred content slot via `<Outlet/>` so each
 *   page (Login / Register) renders only its form/headings.
 * - The right half is identical on every auth screen: dark `--ink`
 *   background, sticker cluster top-right, large display heading bottom-left,
 *   tag-line blurb.
 * - The language switcher renders only when more than one locale is
 *   available (`LANGS.length > 1`), matching the global convention used by
 *   `<AppSidebar/>` so language UX is consistent across the app.
 */
export function AuthLayout() {
  const { t, lang, setLang } = useT();

  return (
    <div
      className="auth-grid"
      style={{ minHeight: "100vh", background: "var(--bg)" }}
    >
      {/* LEFT — form column */}
      <div
        className="auth-left"
        style={{
          display: "flex",
          flexDirection: "column",
          padding: "36px 56px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Brand height={17} withMark showChip />
          {LANGS.length > 1 && (
            <div
              style={{
                display: "inline-flex",
                background: "var(--surface)",
                border: "1px solid var(--line)",
                borderRadius: 6,
                padding: 2,
                fontFamily: "var(--font-mono)",
                fontSize: 11,
              }}
            >
              {LANGS.map((l) => {
                const active = l === lang;
                return (
                  <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: 4,
                      background: active ? "var(--ink)" : "transparent",
                      color: active ? "#fff" : "var(--ink-3)",
                      fontWeight: 500,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Page-owned content — form, heading, etc. */}
        <Outlet />

        <div
          style={{
            color: "var(--ink-4)",
            fontSize: 11,
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.04em",
          }}
        >
          © Foundation Coffee Roasters · Odesa · UA
        </div>
      </div>

      {/* RIGHT — dark brand panel */}
      <div
        className="auth-right"
        style={{
          background: "var(--ink)",
          color: "#fff",
          padding: 56,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Sticker cluster (top-right) */}
        <div
          style={{
            position: "absolute",
            top: 36,
            right: 40,
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            maxWidth: 280,
            justifyContent: "flex-end",
          }}
        >
          <Sticker color="pink" rotate={-6}>
            HoReCa
          </Sticker>
          <Sticker color="green" rotate={4}>
            NET-14
          </Sticker>
          <Sticker color="yellow" rotate={-3}>
            −25%
          </Sticker>
        </div>

        <div
          className="mono"
          style={{
            fontSize: 11,
            letterSpacing: "0.12em",
            color: "rgba(255,255,255,0.55)",
            textTransform: "uppercase",
          }}
        >
          {t("r_for_venues")}
        </div>

        <div>
          <div
            className="display"
            style={{
              fontSize: "clamp(52px, 6.5vw, 78px)",
              lineHeight: 0.94,
              color: "#fff",
              marginBottom: 28,
            }}
          >
            {t("r_hero_a")}
            <br />
            {t("r_hero_b")}
            <br />
            <span style={{ color: "var(--sticker-pink)" }}>
              {t("r_hero_c")}
            </span>
          </div>

          <div
            style={{
              maxWidth: 420,
              color: "rgba(255,255,255,0.65)",
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            {t("brand_panel_copy")}
          </div>
        </div>

        <div
          style={{
            fontSize: 12,
            color: "rgba(255,255,255,0.5)",
            maxWidth: 360,
          }}
        >
          {t("r_bottom_copy")}
        </div>
      </div>
    </div>
  );
}

export default AuthLayout;
